import { useState, useCallback, useEffect } from 'react';
import { Select, Loader } from '@mantine/core';
import { useSectionCompletion } from '../hooks/useSectionCompletion';
import { useLogistics } from '../hooks/useLogistics';
import { showSuccessToast, showErrorToast } from '../utils/notifications';
import { DocumentUpload } from '../components/logistics/DocumentUpload';
import { InsuranceForm } from '../components/logistics/InsuranceForm';
import { PharmacySearch } from '../components/logistics/PharmacySearch';
import type { GovernmentIdType } from '../types/logistics';

const ID_TYPE_OPTIONS = [
  { value: 'drivers-license', label: "Driver's License" },
  { value: 'passport', label: 'Passport' },
  { value: 'state-id', label: 'State ID' },
];

export function Logistics() {
  const { returnToDashboard } = useSectionCompletion('logistics');
  const {
    logisticsData,
    isLoading,
    isSaving,
    error,
    uploadDocument,
    deleteDocument,
    saveAll,
  } = useLogistics();

  // Local form state
  const [idType, setIdType] = useState<GovernmentIdType>(
    logisticsData.governmentIdType || 'drivers-license'
  );
  const [insuranceProvider, setInsuranceProvider] = useState(
    logisticsData.insuranceProvider
  );
  const [memberId, setMemberId] = useState(logisticsData.memberId);
  const [groupNumber, setGroupNumber] = useState(logisticsData.groupNumber);
  const [pharmacy, setPharmacy] = useState(logisticsData.pharmacy || '');

  // Sync state when data loads
  useEffect(() => {
    if (!isLoading) {
      setIdType(logisticsData.governmentIdType || 'drivers-license');
      setInsuranceProvider(logisticsData.insuranceProvider);
      setMemberId(logisticsData.memberId);
      setGroupNumber(logisticsData.groupNumber);
      setPharmacy(logisticsData.pharmacy || '');
    }
  }, [isLoading, logisticsData]);

  // Handle insurance form changes
  const handleInsuranceChange = useCallback(
    (field: 'insuranceProvider' | 'memberId' | 'groupNumber', value: string) => {
      switch (field) {
        case 'insuranceProvider':
          setInsuranceProvider(value);
          break;
        case 'memberId':
          setMemberId(value);
          break;
        case 'groupNumber':
          setGroupNumber(value);
          break;
      }
    },
    []
  );

  // Handle document uploads
  const handleGovernmentIdUpload = useCallback(
    async (file: File) => {
      return uploadDocument(file, 'government-id', idType);
    },
    [uploadDocument, idType]
  );

  const handleInsuranceFrontUpload = useCallback(
    async (file: File) => {
      return uploadDocument(file, 'insurance-card-front');
    },
    [uploadDocument]
  );

  const handleInsuranceBackUpload = useCallback(
    async (file: File) => {
      return uploadDocument(file, 'insurance-card-back');
    },
    [uploadDocument]
  );

  // Delete handlers
  const handleDeleteGovernmentId = useCallback(() => {
    deleteDocument('government-id');
  }, [deleteDocument]);

  const handleDeleteInsuranceFront = useCallback(() => {
    deleteDocument('insurance-card-front');
  }, [deleteDocument]);

  const handleDeleteInsuranceBack = useCallback(() => {
    deleteDocument('insurance-card-back');
  }, [deleteDocument]);

  // Single save handler
  const handleSave = async () => {
    const success = await saveAll({
      insuranceProvider,
      memberId,
      groupNumber,
      pharmacy,
    });

    if (success) {
      showSuccessToast('Logistics information saved');
      returnToDashboard();
    } else {
      showErrorToast('Unable to save. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="animate-fade-in flex items-center justify-center py-12">
        <Loader size="lg" color="cyan" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in">
      <p className="text-gray-400 mb-8">
        Please provide your identification, insurance, and pharmacy information
        to streamline your care experience.
      </p>

      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="space-y-10">
        {/* Government ID Section */}
        <section>
          <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
              1
            </span>
            Government ID
          </h3>
          <div className="space-y-4 pl-8">
            <Select
              label="ID Type"
              placeholder="Select ID type"
              data={ID_TYPE_OPTIONS}
              value={idType}
              onChange={(val) => setIdType(val as GovernmentIdType)}
              classNames={{
                input: 'input',
                label: 'text-sm font-medium text-gray-300 mb-1',
                dropdown: 'bg-gray-900 border-gray-700',
                option: 'hover:bg-gray-800 text-gray-200',
              }}
            />
            <DocumentUpload
              label="Upload ID Photo"
              description="Take a clear photo of your government-issued ID"
              existingUrl={logisticsData.governmentIdUrl}
              onUpload={handleGovernmentIdUpload}
              onRemove={handleDeleteGovernmentId}
            />
          </div>
        </section>

        {/* Insurance Card Section */}
        <section>
          <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
              2
            </span>
            Insurance Card
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-8">
            <DocumentUpload
              label="Card Front"
              description="Photo of the front of your insurance card"
              existingUrl={logisticsData.insuranceCardFrontUrl}
              onUpload={handleInsuranceFrontUpload}
              onRemove={handleDeleteInsuranceFront}
            />
            <DocumentUpload
              label="Card Back"
              description="Photo of the back of your insurance card"
              existingUrl={logisticsData.insuranceCardBackUrl}
              onUpload={handleInsuranceBackUpload}
              onRemove={handleDeleteInsuranceBack}
            />
          </div>
        </section>

        {/* Insurance Information Section */}
        <section>
          <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
              3
            </span>
            Insurance Information
          </h3>
          <div className="pl-8 max-w-md">
            <InsuranceForm
              insuranceProvider={insuranceProvider}
              memberId={memberId}
              groupNumber={groupNumber}
              onChange={handleInsuranceChange}
              disabled={isSaving}
            />
          </div>
        </section>

        {/* Pharmacy Section */}
        <section>
          <h3 className="text-lg font-semibold text-gray-200 mb-4 flex items-center gap-2">
            <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-400 text-sm flex items-center justify-center">
              4
            </span>
            Preferred Pharmacy
            <span className="text-xs text-gray-500 font-normal">(Optional)</span>
          </h3>
          <div className="pl-8">
            <PharmacySearch
              value={pharmacy}
              onChange={setPharmacy}
              disabled={isSaving}
            />
          </div>
        </section>
      </div>

      {/* Action Button */}
      <div className="mt-10 pt-6 border-t border-gray-800 flex items-center justify-end">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="btn disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? (
            <span className="flex items-center gap-2">
              <Loader size={16} color="white" />
              Saving...
            </span>
          ) : (
            'Save'
          )}
        </button>
      </div>
    </div>
  );
}
